import {
  ArrayUtils,
  asQueryString,
  ConfigObjectType,
  DateUtils,
  DlMuiButton,
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  StringUtils,
  useLgOrLarger,
  useLocalStorage,
  ExportUtils,
  useCurrentLocale,
  NoDataMessageWithIcon,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  CHART_MONTH_TICKFORMAT,
  ChartColor,
  clampPageNumber,
  DEFAULT_PAGE_SIZE,
  Formatters,
  getDefaultMTOptions,
  getLineChartItems,
  getTableMaxHeight,
  HaidiSchema,
  JustTooltipFormatter,
  LightChart,
  LightChartImageDownloadProps,
  LightChartLayout,
  LightChartLegend2,
  LightLineChartTrace,
  LightSelectedFilters,
  LightTable,
  LTColumn,
  MaterialTableWrapper,
  ProgressBarFormatter,
  TableSorting,
  TooltipWrapper,
  useLightTableSort,
  useSnomedLabels,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {get, sortBy} from 'lodash';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';

import {useDepartmentReportHaisColumns} from './DepartmentReportTablesAndChart';
import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {HaiListLinkFilters} from '../hai';

export type HaiByPathogensData = HaidiSchema.components['schemas']['HaiPathogenReportDto'];

// haisByPathogen table
type HaisByPathogenRowType = HaiByPathogensData['haiPathogenOverview'][0];
type CountPercentFormatterProps = {
  data: HaisByPathogenRowType['total'];
  formatters: Formatters;
  big: boolean;
};
const CountPercentFormatter: React.FC<CountPercentFormatterProps> = (props) => {
  const {data, formatters, big} = props;
  const {haiPathogenCount, percentage, percentageTooltip} = data;
  const {formatFloat} = formatters;
  const percentString = `${formatFloat(percentage)} %`;
  return (
    <TooltipWrapper
      tooltip={percentageTooltip}
      style={{width: '100%'}}
      wrapperStyle={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, maxWidth: 190}}
    >
      <span
        style={
          big
            ? {
                minWidth: 29,
                textAlign: 'left', // PDH-1589: align count with the left-aligned header (bar stays put via minWidth)
              }
            : undefined
        }
      >
        {haiPathogenCount ?? EMPTY}
      </span>
      <ProgressBarFormatter
        {...AG_GRID_COMPATIBILITY}
        value={percentString ?? EMPTY}
        tooltipOverride={''}
        progressPercent={percentage}
        style={{flex: 1}}
        paddingLeft={big ? 6 : 4}
      />
    </TooltipWrapper>
  );
};
const EMPTY = '-';
type HaisByPathogenExtraColumn = {
  index: number;
  haiType: string;
};
function useHaisByPathogenColumns(
  formatters: Formatters,
  snomedLabels: Record<string, string>,
  extraColumns: HaisByPathogenExtraColumn[],
  defaultSort: TableSorting
): LTColumn<HaisByPathogenRowType>[] {
  const {t, tEnum} = formatters;
  const lgOrLarger = useLgOrLarger();
  const extraColumnStyle: Partial<LTColumn<HaisByPathogenRowType>> = {
    flex: '1 1 0',
    minWidth: 80, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the metric columns
  };
  const columns: LTColumn<HaisByPathogenRowType>[] = [
    {
      field: 'pathogenCode',
      title: t('reports.haiCountPerPathogen.pathogen'),
      render: (data) => {
        const pathogenString = snomedLabels[data.pathogenCode] ?? data.pathogenCode;
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={pathogenString ?? EMPTY}
            tooltipOverride={pathogenString}
            bold
            singleLine
            alignCenter
          />
        );
      },
      customSort: (row1, row2) => {
        const key = (row: HaisByPathogenRowType) => snomedLabels[row.pathogenCode] ?? row.pathogenCode;
        return StringUtils.compare(key(row1), key(row2));
      },
      sorting: true,
      flex: '1.75 1 0px',
      minWidth: 122,
    },
    {
      field: 'total',
      title: t('reports.haiCountPerPathogen.total'),
      render: (data) => {
        return <CountPercentFormatter data={data.total} formatters={formatters} big={lgOrLarger} />;
      },
      customSort: (row1, row2) => {
        const key = (row: HaisByPathogenRowType) => row.total.haiPathogenCount;
        return StringUtils.compare(key(row1), key(row2));
      },
      sorting: true,
      ...extraColumnStyle,
    },
  ];
  const getExtraColumnTitle = (v: HaisByPathogenExtraColumn) => {
    const {haiType} = v;
    const haiTypeLabel = tEnum('HaiTypeEnum', haiType);
    if (v.haiType === 'OTHER') {
      return {
        title: haiTypeLabel,
        tooltip: haiTypeLabel,
      };
    }
    return {
      title: haiType.replace('_', '-'),
      tooltip: haiTypeLabel,
    };
  };
  extraColumns.forEach((v) => {
    columns.push({
      field: v.haiType,
      ...getExtraColumnTitle(v),
      render: (data) => {
        return <CountPercentFormatter data={data.results[v.index]} formatters={formatters} big={lgOrLarger} />;
      },
      customSort: (row1, row2) => {
        const key = (row: HaisByPathogenRowType) => row.results[v.index].haiPathogenCount;
        return StringUtils.compare(key(row1), key(row2));
      },
      sorting: true,
      ...extraColumnStyle,
    });
  });
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort:
      v.customSort ??
      ((row1, row2, _type) => {
        const key = (row: HaisByPathogenRowType) => get(row, v.field ?? '');
        return StringUtils.compare(key(row1), key(row2));
      }),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

type Props = {
  config: ConfigObjectType;
  muiStyles: any;
  storageIdPrefix: string;
  filters: any;
  changeFilters: (newFilters: any) => void;
  hasHaisByPathogenSorting: boolean;
  hasHaisSorting: boolean;
  handleResetTopSorting: () => void;
  handleResetHaisSorting: () => void;
  hasFilters: boolean;
  handleResetFilters: () => void;
  formatters: Formatters;
  isLoading: boolean;
  reportData: HaiByPathogensData | undefined;
};
export const HaiByPathogensReportTablesAndChart: React.FC<Props> = (props) => {
  const {
    config,
    muiStyles,
    storageIdPrefix,
    filters,
    changeFilters,
    hasHaisByPathogenSorting,
    hasHaisSorting,
    handleResetTopSorting,
    hasFilters,
    handleResetFilters,
    handleResetHaisSorting,
    formatters,
    isLoading,
    reportData,
  } = props;
  const {t} = formatters;
  const haisByPathogenExtraColumns: HaisByPathogenExtraColumn[] = useMemo(() => {
    const accSet = new Set<string>();
    const acc: HaisByPathogenExtraColumn[] = [];
    (reportData?.haiPathogenOverview ?? []).forEach((row) => {
      row.results.forEach((cell, i) => {
        if (!accSet.has(cell.haiType)) {
          accSet.add(cell.haiType);
          acc.push({
            haiType: cell.haiType,
            index: i,
          });
        }
      });
    });
    return sortBy(acc, (v) => v.index);
  }, [reportData]);
  const {snomedLabels /* snomedLabelsLoading */} = useSnomedLabels(config);
  const haisByPathogenColumns = useHaisByPathogenColumns(formatters, snomedLabels, haisByPathogenExtraColumns, filters.haisByPathogenSort);
  const sortedHaisByPathogenData = useLightTableSort(reportData?.haiPathogenOverview ?? [], haisByPathogenColumns, [
    filters.haisByPathogenSort.field ? (filters.haisByPathogenSort as TableSorting) : {field: 'total', direction: 'desc'},
    {field: 'pathogenCode', direction: 'asc'},
  ]);
  const pathogenCodeToTotal = useMemo(() => {
    const acc: Record<string, number> = {};
    (reportData?.haiPathogenOverview ?? []).forEach((v) => {
      acc[v.pathogenCode] = v.total.haiPathogenCount;
    });
    return acc;
  }, [reportData]);
  const {standardTypeTranslations} = useStandardTypes({config});
  // hais
  const haisColumns = useDepartmentReportHaisColumns(snomedLabels, formatters, filters.haisSort, true, standardTypeTranslations);
  // chart
  const chartLayout: LightChartLayout = useMemo(() => {
    // const [chartStartDate, chartEndDate] = chartRange;
    // const chartEndDateNextMidnight = DateUtils.nextMidnightIsoString(chartEndDate ?? '');
    return {
      margin: {
        b: 42,
      },
      xaxis: {
        type: 'date',
        tickformat: CHART_MONTH_TICKFORMAT,
        automargin: true,
      },
      yaxis: {
        rangemode: 'tozero',
        automargin: true,
        fixedrange: true,
      },
      height: 350,
      showlegend: false,
    };
  }, []);
  const chartImageDownload: LightChartImageDownloadProps = {
    showlegend: true,
    title: {
      text: t('reports.haiPathogenTrend'),
    },
  };
  const chartColorHiddenMap = useRef({} as Record<string | number, boolean>);
  const [hiddenMapNonce, _rerender] = useState(0);
  const onSetHiddenMap = useCallback(() => {
    _rerender((v) => v + 1);
  }, [_rerender]);
  const currentLocale = useCurrentLocale();
  const chartColors: ChartColor[] = useMemo(() => {
    // https://observablehq.com/@shan/oklab-color-wheel
    // NOTE: COLOR_SET.length must be a power of two
    const COLOR_SET = [
      '#ff656f',
      '#ff7700',
      '#ff9200',
      '#fcaf00',
      '#c3ca00',
      '#6dde00',
      '#00ea79',
      '#00edc4',
      '#00e6ff',
      '#00d8ff',
      '#00c3ff',
      '#82abff',
      '#c794ff',
      '#f880ff',
      '#ff6ff6',
      '#ff63b7',
    ];
    const COLOR_START_INDEX = 2;
    const COLOR_STEP = 3; // NOTE: COLOR_STEP must be an odd number
    const pathogenCodes = Array.from(new Set((reportData?.haiPathogenTrend ?? []).map((v) => v.pathogenCode)));
    const sortedPathogenCodes = pathogenCodes.sort((a, b) => {
      const comparisons = [
        -StringUtils.compare(pathogenCodeToTotal[a], pathogenCodeToTotal[b]),
        StringUtils.localeCompare(snomedLabels[a], snomedLabels[b], currentLocale),
      ];
      return comparisons.reduce((acc, v) => acc || v, 0);
    });
    const acc = sortedPathogenCodes.map((pathogenCode, i) => {
      const color: ChartColor = {
        id: pathogenCode,
        label: snomedLabels[pathogenCode] ?? String(pathogenCode),
        color: COLOR_SET[(COLOR_START_INDEX + i * COLOR_STEP) % COLOR_SET.length],
        hidden: chartColorHiddenMap.current[pathogenCode],
      };
      return color;
    });
    return acc;
  }, [reportData, pathogenCodeToTotal, snomedLabels, currentLocale, hiddenMapNonce, chartColorHiddenMap]); // eslint-disable-line
  const parseChartData = (v: HaiByPathogensData['haiPathogenTrend'][0]) => ({
    timestamp: DateUtils.fromReverseMonthFormat(v.month ?? '').toISOString(),
    count: v.haiPathogenCount ?? 0,
  });
  const unboundChartData = useMemo(() => {
    const sortedPathogenTrend = sortBy(reportData?.haiPathogenTrend ?? [], (v) => v.month);
    return ArrayUtils.zipAll(
      ...chartColors.map((color) => sortedPathogenTrend.filter((v) => v.pathogenCode === color.id).map(parseChartData))
    );
  }, [reportData, chartColors]);
  const chartData: LightLineChartTrace[] = useMemo(() => {
    const {formatFloat} = formatters;
    return getLineChartItems(unboundChartData, chartColors, (v, i) => ({
      x: v[i]?.timestamp ?? '',
      y: v[i]?.count ?? 0,
      tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${chartColors[i].label}</b>: ${formatFloat(
        v[i]?.count ?? 0
      )}`,
    })).map((v) => ({...v, cliponaxis: false}));
  }, [unboundChartData, chartColors, formatters]);
  // hais
  const lastHaisData = useMemo(() => reportData?.lastHais ?? [], [reportData]);
  const haisLinkQuery = useMemo(() => {
    let states = ['YES', 'COL'];
    if (filters.report.haiState !== 'ALL') {
      states = [filters.report.haiState];
    }
    const acc: HaiListLinkFilters = {
      detectionDate: {
        startDate: filters.report.dateFrom,
        endDate: filters.report.dateTo,
      },
      states,
      organizationIds: filters.report.organizationIds,
      types: filters.report.haiTypes,
      pathogenIds: filters.report.pathogenIds,
    };
    return {hais: acc};
  }, [filters]);
  // split
  /* const splitA: LightSplitLayoutProps = {
    storageId: '/LightSplitLayout.departmentReport.A',
    initSizes: [50, 50],
    minSizes: [100, 100],
  }; */
  const splitB: LightSplitLayoutProps = {
    storageId: '/LightSplitLayout.haiByPathogens.B',
    initSizes: [50, 50],
    minSizes: [100, 100],
  };
  const [defaultPathogensScroll, setDefaultPathogensScroll] = useLocalStorage(`${storageIdPrefix}.pathogensScroll`, 0);
  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.haiXlsxExport.replace('{reportName}', 'HAI_PATHOGEN_REPORT'),
      filters: filters.report,
      locale: currentLocale,
    });
  }, [config.service.url.haiXlsxExport, currentLocale, filters]);
  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);
  return (
    <React.Fragment key='DepartmentReportTablesAndChart'>
      <div css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'haisByPathogenIconMultiColor'}
          label={'reports.haiCountPerPathogen'}
          useFixedViewBox='0 0 11 16'
          iconCss={muiStyles.headerIcon}
        >
          <LightSelectedFilters
            hasFilters={hasFilters}
            handleResetFilters={handleResetFilters}
            hasSorting={hasHaisByPathogenSorting}
            handleResetSorting={handleResetTopSorting}
            hasXlsxExport
            handleXlsxExport={() => downloadXlsxExport()}
          />
        </IconHeaderWithSearch>
        <LightTable
          style={{flex: 5, maxHeight: getTableMaxHeight(15)}}
          defaultScroll={{to: defaultPathogensScroll}}
          onScroll={(event) => setDefaultPathogensScroll((event.target as HTMLDivElement).scrollTop)}
          /* options={{
            ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
            paging: false,
          }} */
          variant='big'
          /* variantPadding='small'
          useMaxHeight */
          columns={haisByPathogenColumns}
          data={sortedHaisByPathogenData}
          isLoading={isLoading}
          onOrderChange={(field, direction) => {
            changeFilters({
              haisByPathogenSort: field
                ? {
                    field,
                    direction,
                  }
                : {},
            });
          }}
          controlledSort={(() => {
            return filters.haisByPathogenSort;
          })()}
          centerLoadingSpinner
        />
      </div>
      <LightSplitLayout {...splitB} style={{height: 'auto'}}>
        <div css={muiStyles.contentBox} style={{height: 'auto'}}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'reports.haiPathogenTrend'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightChartLegend2 items={chartColors} hiddenMap={chartColorHiddenMap} onSetHiddenMap={onSetHiddenMap} />
          </IconHeaderWithSearch>
          <LightChart
            key={JSON.stringify(chartLayout.xaxis.range)}
            data={chartData}
            layout={chartLayout}
            config={{}}
            imageDownload={chartImageDownload}
            noDataComponent={noDataComponent}
          />
        </div>
        <div css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'reports.lastHais'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters hasSorting={hasHaisSorting} handleResetSorting={handleResetHaisSorting} />
            <Link css={muiStyles.showAllLink} tabIndex={0} to={`/hais?${asQueryString(haisLinkQuery)}`}>
              <DlMuiButton
                type='chip-filled'
                title='dashboard.showAll'
                onClick={() => {
                  // noop
                }}
              />
            </Link>
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
              pageSize: filters.haisPaging.pageSize ?? DEFAULT_PAGE_SIZE,
            }}
            variant='big'
            variantPadding='small'
            singleLineColumnLabels
            useMaxHeight
            columns={haisColumns}
            data={lastHaisData}
            isLoading={isLoading}
            controlledPage={filters.haisPaging.page ?? 0}
            onChangePage={(page, pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  pageSize,
                  page: clampPageNumber(filters.haisPaging.page, pageSize, lastHaisData.length),
                },
              })
            }
            onOrderChange={(colIndex, direction) => {
              const columnId = haisColumns[colIndex].field as string;
              changeFilters({
                haisSort: {
                  field: columnId,
                  direction,
                },
              });
            }}
            controlledSort={(() => {
              return filters.haisSort;
            })()}
          />
        </div>
      </LightSplitLayout>
    </React.Fragment>
  );
};
